"use client";

import { useEffect, useState } from "react";
import { Play, X } from "lucide-react";

const SEEN_KEY = "mva-launch-video-seen";

const isFile = (u: string) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(u);

function embedUrl(u: string) {
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&mute=1&rel=0`;
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1`;
  return u;
}

/** Launch video - pops up when someone opens the site (once per browser session). */
export function H9LaunchVideo({ src, title = "Launch video" }: { src: string; title?: string }) {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [filePortrait, setFilePortrait] = useState(false);
  const portrait = isFile(src) ? filePortrait : /youtube\.com\/shorts\//i.test(src);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-mount: the async loader flips loading state before awaiting the API
    setReady(true);
    try {
      if (sessionStorage.getItem(SEEN_KEY)) return;
    } catch {}
    // Open after the visitor's first interaction, not on load - the video player's third-party cookies and
    // scripts would otherwise slow the first paint and be flagged by Lighthouse.
    let id: ReturnType<typeof setTimeout> | undefined;
    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    const onFirst = () => {
      events.forEach((e) => window.removeEventListener(e, onFirst));
      id = setTimeout(() => setOpen(true), 800);
    };
    events.forEach((e) => window.addEventListener(e, onFirst, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onFirst));
      clearTimeout(id);
    };
  }, []);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {}
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) {
    if (!ready) return null;
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Watch launch video"
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 rounded-full bg-brand-700 py-2.5 pl-3 pr-4 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-brand-700">
          <Play className="h-4 w-4 fill-current" />
        </span>
        Watch video
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={close}
    >
      <div className={portrait ? "relative" : "relative w-full max-w-3xl"} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={close}
          aria-label="Close video"
          className="absolute -top-11 right-0 rounded-full bg-white/90 p-2 text-black"
        >
          <X className="h-4 w-4" />
        </button>
        {/* reels / shorts / vertical files get a 9:16 frame that fits the screen - no black side bars */}
        <div
          className={`overflow-hidden rounded-2xl bg-black ${portrait ? "mx-auto aspect-9/16 h-[min(80vh,720px)]" : "aspect-video"}`}
        >
          {isFile(src) ? (
            <video
              src={src}
              controls
              controlsList="nodownload noremoteplayback"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              autoPlay
              muted
              playsInline
              onLoadedMetadata={(e) => setFilePortrait(e.currentTarget.videoHeight > e.currentTarget.videoWidth)}
              className="h-full w-full"
            />
          ) : (
            <iframe
              src={embedUrl(src)}
              title={title}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              className="h-full w-full"
            />
          )}
        </div>
      </div>
    </div>
  );
}
